import React, { useState, useEffect } from "react";
import { Button } from "src/components/ui/button";
import { 
  Card, 
  CardContent, 
  CardHeader, 
  CardTitle 
} from "src/components/ui/card";
import { Input } from "src/components/ui/input";
import { Label } from "src/components/ui/label";
import { UserPlus, X, Trash2, Check, Shield, UserCog, ArrowLeft, Key, Power, Users, Activity } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { apiConfig } from '../config/apiConfig';
import { TableContainer } from '../components/ui/table-container';
import toast from "react-hot-toast";
import { UserAvatar } from '../components/UserAvatar';
import { 
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "src/components/ui/tabs";
import { Badge } from "src/components/ui/badge";
import { 
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";

// Update the User interface to include all necessary properties
interface User {
  id: number;
  username: string;
  email: string;
  first_name: string;
  last_name: string;
  is_admin: boolean;
  is_debater: boolean;
  is_adjudicator: boolean;
  is_staff?: boolean;
  is_superuser?: boolean;
  has_token?: boolean;
}

interface ActiveUser {
  tracker_id: number;
  user_id: number;
  username: string;
  email: string;
  first_name: string;
  last_name: string;
  is_admin: boolean;
  is_staff: boolean;
  is_debater: boolean;
  is_adjudicator: boolean;
  last_activity: string;
  login_time: string | null;
  session_key: string;
}

interface EditableUserData {
  username: string;
  email: string;
  first_name: string;
  last_name: string;
  is_admin: boolean;
  is_debater: boolean;
  is_adjudicator: boolean;
}

const initialNewUser = {
  username: "",
  email: "",
  first_name: "",
  last_name: "",
  password: "",
  is_admin: false,
  is_debater: false,
  is_adjudicator: false
};

const UsersPanel: React.FC = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState<User[]>([]);
  const [activeUsers, setActiveUsers] = useState<ActiveUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeUsersLoading, setActiveUsersLoading] = useState(true);
  const [error, setError] = useState("");
  const [showNewUserForm, setShowNewUserForm] = useState(false);
  const [newUser, setNewUser] = useState(initialNewUser);
  const [editingUserId, setEditingUserId] = useState<number | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [activeSearchTerm, setActiveSearchTerm] = useState("");
  const [editData, setEditData] = useState<EditableUserData | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [isAdmin, setIsAdmin] = useState<boolean>(false);
  const [showForceLogoutDialog, setShowForceLogoutDialog] = useState(false);
  const [userToLogout, setUserToLogout] = useState<ActiveUser | null>(null);
  const [forceLoggingOut, setForceLoggingOut] = useState(false);
  // Add state for delete dialog
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [userToDelete, setUserToDelete] = useState<User | null>(null);
  
  // Add pagination state:
  const [userPage, setUserPage] = useState(1);
  const USERS_PER_PAGE = 10;

  // Reset page to 1 when search term changes:
  useEffect(() => { setUserPage(1); }, [searchTerm]);

  // Filter functions
  const filteredUsers = users.filter(user => 
    user.username.toLowerCase().includes(searchTerm.toLowerCase()) || 
    user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    `${user.first_name} ${user.last_name}`.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredActiveUsers = activeUsers.filter(user =>
    user.username.toLowerCase().includes(activeSearchTerm.toLowerCase()) ||
    user.email.toLowerCase().includes(activeSearchTerm.toLowerCase()) ||
    `${user.first_name} ${user.last_name}`.toLowerCase().includes(activeSearchTerm.toLowerCase())
  );

  // Paginate filtered users:
  const paginatedUsers = filteredUsers.slice((userPage - 1) * USERS_PER_PAGE, userPage * USERS_PER_PAGE);
  const userTotalPages = Math.ceil(filteredUsers.length / USERS_PER_PAGE);

  // Navigation function
  const handleBack = () => {
    navigate('/dashboard');
  };
  
  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { 
      opacity: 1,
      transition: { 
        duration: 0.3,
        when: "beforeChildren",
        staggerChildren: 0.1
      }
    },
    exit: { opacity: 0, transition: { duration: 0.3 } }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: { y: 0, opacity: 1, transition: { duration: 0.3 } }
  };

  useEffect(() => {
    fetchUsers();
    fetchActiveUsers();
    
    // Check admin status from localStorage
    const adminStatus = localStorage.getItem("is_admin") === "true";
    setIsAdmin(adminStatus);

    // Set up refresh interval for active users (every 30 seconds)
    const refreshInterval = setInterval(() => {
      fetchActiveUsers();
    }, 30000);

    return () => clearInterval(refreshInterval);
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      
      const response = await fetch(`${apiConfig.baseURL}/api/admin/users/`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Token ${token}`
        },
      });

      if (!response.ok) {
        throw new Error("Failed to fetch users");
      }

      const data = await response.json();
      setUsers(data.users || []);
      setError("");
    } catch (err: any) {
      setError(err.message || "An error occurred while fetching users");
      console.error("Error fetching users:", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchActiveUsers = async () => {
    try {
      setActiveUsersLoading(true);
      const token = localStorage.getItem("token");
      
      const response = await fetch(`${apiConfig.baseURL}/api/admin/active-users/`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Token ${token}`
        },
      });

      if (!response.ok) {
        throw new Error("Failed to fetch active users");
      }

      const data = await response.json();
      setActiveUsers(data.active_users || []);
    } catch (err: any) {
      console.error("Error fetching active users:", err);
      // Don't show error for active users fetch as it's not critical
    } finally {
      setActiveUsersLoading(false);
    }
  };

  const handleForceLogout = async () => {
    if (!userToLogout) return;

    try {
      setForceLoggingOut(true);
      const token = localStorage.getItem("token");
      
      const response = await fetch(`${apiConfig.baseURL}/api/admin/force-logout/${userToLogout.user_id}/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Token ${token}`
        },
        body: JSON.stringify({
          delete_token: true // Also delete their auth token
        })
      });

      if (!response.ok) {
        throw new Error("Failed to force logout user");
      }

      const data = await response.json();
      toast.success(`Successfully logged out ${userToLogout.username}`);
      
      // Remove user from active users list
      setActiveUsers(prev => prev.filter(u => u.user_id !== userToLogout.user_id));
      
      // Close dialog
      setShowForceLogoutDialog(false);
      setUserToLogout(null);
      
    } catch (err: any) {
      console.error("Error forcing logout:", err);
      toast.error(err.message || "Failed to force logout user");
    } finally {
      setForceLoggingOut(false);
    }
  };

  const promptForceLogout = (user: ActiveUser) => {
    setUserToLogout(user);
    setShowForceLogoutDialog(true);
  };

  // Update the handleCreateUser function to use the secure command execution approach
  const handleCreateUser = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Form validation
    if (!newUser.username || !newUser.password || !newUser.email) {
      setError('Username, password, and email are required.');
      return;
    }
    
    try {
      setSubmitting(true);
      setError('');
      
      // Call the special endpoint that will execute the proper Django command
      const response = await fetchWithAuth('/admin/create-user-secure/', {
        method: 'POST',
        body: JSON.stringify({
          username: newUser.username,
          password: newUser.password,
          email: newUser.email,
          first_name: newUser.first_name || '',
          last_name: newUser.last_name || '',
          is_staff: Boolean(newUser.is_admin),
          is_superuser: Boolean(newUser.is_admin),
          is_admin: Boolean(newUser.is_admin),
          is_debater: Boolean(newUser.is_debater),
          is_adjudicator: Boolean(newUser.is_adjudicator)
        })
      });
      
      console.log("User created successfully:", response);
      
      // Add the new user to the list
      if (response && response.user) {
        const newUserWithToken = {
          ...response.user,
          has_token: true
        } as User;
        
        setUsers(prevUsers => [...prevUsers, newUserWithToken]);
        toast.success('User created successfully with authentication token');
      }
      
      // Reset the form
      setNewUser(initialNewUser);
      setShowNewUserForm(false);
      
    } catch (err: any) {
      console.error("Error creating user:", err);
      setError(err.message || 'Failed to create user');
    } finally {
      setSubmitting(false);
    }
  };

  // Update handleDeleteUser to use dialog
  const handleDeleteUser = (userId: number) => {
    const user = users.find(u => u.id === userId);
    setUserToDelete(user || null);
    setShowDeleteDialog(true);
  };
  const confirmDeleteUser = async () => {
    if (!userToDelete) return;
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${apiConfig.baseURL}/api/admin/users/${userToDelete.id}/`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Token ${token}`
        },
      });

      if (!response.ok) {
        let errorMessage = `Failed with status: ${response.status}`;
        try {
          const errorData = await response.json();
          errorMessage = errorData.error || errorData.detail || errorData.message || errorMessage;
        } catch (parseErr) {
          errorMessage = response.statusText || errorMessage;
        }
        throw new Error(errorMessage);
      }

      // For DELETE requests, we don't need to parse the response body
      // Just check if the status is 204 (No Content) or 200 (OK)
      if (response.status === 204 || response.status === 200) {
        // Success - update UI
        setUsers(users.filter(user => user.id !== userToDelete.id));
        setError("");
        toast.success('User deleted successfully');
      } else {
        // Unexpected status code
        throw new Error(`Unexpected response status: ${response.status}`);
      }

      setShowDeleteDialog(false);
      setUserToDelete(null);
    } catch (err: any) {
      setError(err.message || "An error occurred while deleting the user");
      console.error("Error deleting user:", err);
      toast.error('Failed to delete user');
      setShowDeleteDialog(false);
      setUserToDelete(null);
    }
  };

  const handleUpdateUser = async (userId: number, userData: Partial<User>) => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      
      const response = await fetch(`${apiConfig.baseURL}/api/admin/users/${userId}/`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Token ${token}`
        },
        body: JSON.stringify(userData),
      });

      if (!response.ok) {
        throw new Error("Failed to update user");
      }

      // Update user in state
      setUsers(users.map(user => 
        user.id === userId ? { ...user, ...userData } : user
      ));
      
      // Reset edit states
      setEditingUserId(null);
      setEditData(null);
      setError("");
    } catch (err: any) {
      setError(err.message || "An error occurred while updating the user");
      console.error("Error updating user:", err);
    } finally {
      setLoading(false);
    }
  };

  // Add a new function to start editing a user
  const startEditingUser = (user: User) => {
    setEditingUserId(user.id);
    setEditData({
      username: user.username,
      email: user.email,
      first_name: user.first_name || '',
      last_name: user.last_name || '',
      is_admin: user.is_admin,
      is_debater: user.is_debater,
      is_adjudicator: user.is_adjudicator
    });
  };

  // Add a function to cancel editing
  const cancelEditing = () => {
    setEditingUserId(null);
    setEditData(null);
  };

  // Add a function to promote user to admin
  const promoteToAdmin = async (userId: number) => {
    if (window.confirm("Are you sure you want to promote this user to admin?")) {
      await handleUpdateUser(userId, { is_admin: true });
    }
  };

  // Update the runFixTokenTable function to use an available toast method
  const runFixTokenTable = async () => {
    try {
      // Replace toast.info with toast (default) or toast.custom
      toast("Attempting to fix token table...", {
        icon: "ℹ️", // Info icon
        style: {
          background: "#3b82f6", // Blue background
          color: "#fff"          // White text
        },
      });
      
      // Call a custom admin endpoint that runs the command
      const response = await fetchWithAuth('/admin/fix-token-table/', {
        method: 'POST',
        body: JSON.stringify({})
      });
      
      toast.success("Token table fixed successfully!");
      console.log("Fix token table response:", response);
      
    } catch (err) {
      console.error("Failed to fix token table:", err);
      toast.error("Failed to fix token table. Please contact administrator.");
    }
  };

  // Add this function to fix user passwords
  const fixUserPasswords = async () => {
    try {
      toast("Attempting to fix user passwords...", {
        icon: "🔧",
        style: {
          background: "#3b82f6",
          color: "#fff"
        },
      });
      
      // Call the endpoint to fix user passwords
      const response = await fetchWithAuth('/admin/fix-user-passwords/', {
        method: 'POST',
        body: JSON.stringify({})
      });
      
      toast.success("User passwords fixed successfully!");
      console.log("Fix user passwords response:", response);
      
    } catch (err) {
      console.error("Failed to fix user passwords:", err);
      toast.error("Failed to fix user passwords. Please contact administrator.");
    }
  };

  const formatLastActivity = (lastActivity: string) => {
    const now = new Date();
    const activity = new Date(lastActivity);
    const diffMs = now.getTime() - activity.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    
    const diffDays = Math.floor(diffHours / 24);
    return `${diffDays}d ago`;
  };

  return (
    <motion.div 
      className="flex justify-center items-start w-full p-2 sm:p-4"
      initial="hidden"
      animate="visible"
      exit="exit"
      variants={containerVariants}
    >
      <Card className="w-full max-w-7xl transition-colors duration-200 shadow-lg rounded-lg">
        <CardHeader className="flex flex-col sm:flex-row items-start sm:items-center justify-between p-4 sm:p-6 space-y-4 sm:space-y-0">
          <div className="flex items-center">
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={handleBack}
              className="mr-2"
            >
              <ArrowLeft size={16} />
            </Button>
            <CardTitle className="text-xl sm:text-2xl font-bold">
              User Management
            </CardTitle>
          </div>
          <div className="flex flex-col sm:flex-row w-full sm:w-auto space-y-2 sm:space-y-0 sm:space-x-2">
            <Button 
              onClick={() => setShowNewUserForm(true)}
              variant="action"
              className="w-full sm:w-auto"
            >
              <UserPlus size={16} className="mr-2" />
              <span>Add User</span>
            </Button>
          </div>
        </CardHeader>

        <CardContent className="p-4 sm:p-6">
          {error && (
            <motion.div 
              variants={itemVariants}
              className="mb-4 p-4 bg-red-500 bg-opacity-10 border border-red-500 rounded-lg text-red-500"
            >
              {error}
            </motion.div>
          )}

          <Tabs defaultValue="all-users" className="w-full">
            <TabsList className="grid w-full grid-cols-2 mb-6">
              <TabsTrigger value="all-users" className="flex items-center space-x-2">
                <Users size={16} />
                <span className="hidden sm:inline">All Users</span>
                <span className="sm:hidden">All</span>
                <Badge variant="secondary" className="ml-1">{users.length}</Badge>
              </TabsTrigger>
              <TabsTrigger value="active-users" className="flex items-center space-x-2">
                <Activity size={16} />
                <span className="hidden sm:inline">Active Users</span>
                <span className="sm:hidden">Active</span>
                <Badge variant="secondary" className="ml-1">{activeUsers.length}</Badge>
              </TabsTrigger>
            </TabsList>

            <TabsContent value="all-users" className="space-y-4">
              {/* Search bar for all users */}
              <div className="flex flex-col sm:flex-row gap-4">
                <Input 
                  placeholder="Search users..." 
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="flex-1 dark:text-white light:text-gray-800"
                />
              </div>

              {/* All Users Content */}
              {loading ? (
                <div className="flex justify-center items-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Desktop Table View */}
                  <div className="hidden lg:block">
                    <TableContainer>
                      <table className="w-full">
                        <thead>
                          <tr className="border-b dark:border-gray-700 light:border-gray-200">
                            <th className="text-left p-3 font-medium dark:text-white light:text-gray-800">User</th>
                            <th className="text-left p-3 font-medium dark:text-white light:text-gray-800">Email</th>
                            <th className="text-left p-3 font-medium dark:text-white light:text-gray-800">Roles</th>
                            <th className="text-left p-3 font-medium dark:text-white light:text-gray-800">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {paginatedUsers.map((user) => (
                            <tr key={user.id} className="border-b dark:border-gray-700 light:border-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700">
                              <td className="p-3">
                                <div className="flex items-center space-x-3">
                                  <UserAvatar username={user.username} size="sm" />
                                  <div>
                                    <div className="font-medium dark:text-white light:text-gray-800">
                                      {user.username}
                                    </div>
                                    <div className="text-sm text-gray-500">
                                      {user.first_name} {user.last_name}
                                    </div>
                                  </div>
                                </div>
                              </td>
                              <td className="p-3 dark:text-white light:text-gray-800">{user.email}</td>
                              <td className="p-3">
                                <div className="flex flex-wrap gap-1">
                                  {user.is_admin && <Badge className="bg-red-500 text-white">Admin</Badge>}
                                  {user.is_staff && <Badge className="bg-purple-500 text-white">Staff</Badge>}
                                  {user.is_adjudicator && <Badge className="bg-green-500 text-white">Adjudicator</Badge>}
                                  {user.is_debater && <Badge className="bg-blue-500 text-white">Debater</Badge>}
                                </div>
                              </td>
                              <td className="p-3">
                                <div className="flex items-center space-x-2">
                                  <Button variant="ghost" size="sm" onClick={() => startEditingUser(user)}>
                                    <UserCog size={16} />
                                  </Button>
                                  <Button variant="ghost" size="sm" onClick={() => handleDeleteUser(user.id)} className="text-red-500 hover:text-red-700">
                                    <Trash2 size={16} />
                                  </Button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </TableContainer>
                  </div>

                  {/* Mobile Card View */}
                  <div className="lg:hidden space-y-3">
                    {paginatedUsers.map((user) => (
                      <Card key={user.id} className="p-4">
                        <div className="flex items-start justify-between">
                          <div className="flex items-center space-x-3 flex-1">
                            <UserAvatar username={user.username} size="sm" />
                            <div className="flex-1 min-w-0">
                              <div className="font-medium dark:text-white light:text-gray-800 truncate">
                                {user.username}
                              </div>
                              <div className="text-sm text-gray-500 truncate">
                                {user.first_name} {user.last_name}
                              </div>
                              <div className="text-sm text-gray-400 truncate">
                                {user.email}
                              </div>
                              <div className="flex flex-wrap gap-1 mt-2">
                                {user.is_admin && <Badge className="bg-red-500 text-white text-xs">Admin</Badge>}
                                {user.is_staff && <Badge className="bg-purple-500 text-white text-xs">Staff</Badge>}
                                {user.is_adjudicator && <Badge className="bg-green-500 text-white text-xs">Adj</Badge>}
                                {user.is_debater && <Badge className="bg-blue-500 text-white text-xs">Debater</Badge>}
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center space-x-2">
                            <Button variant="ghost" size="sm" onClick={() => startEditingUser(user)}>
                              <UserCog size={16} />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => handleDeleteUser(user.id)} className="text-red-500 hover:text-red-700">
                              <Trash2 size={16} />
                            </Button>
                          </div>
                        </div>
                      </Card>
                    ))}
                  </div>

                  {userTotalPages > 1 && (
                    <div className="flex justify-center items-center gap-2 mt-6">
                      <Button
                        variant="outline"
                        onClick={() => setUserPage(prev => Math.max(1, prev - 1))}
                        disabled={userPage === 1}
                        className="flex items-center gap-1"
                      >
                        Previous
                      </Button>
                      <span className="text-sm text-gray-700 dark:text-gray-300">
                        Page {userPage} of {userTotalPages}
                      </span>
                      <Button
                        variant="outline"
                        onClick={() => setUserPage(prev => Math.min(userTotalPages, prev + 1))}
                        disabled={userPage === userTotalPages}
                        className="flex items-center gap-1"
                      >
                        Next
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </TabsContent>

            <TabsContent value="active-users" className="space-y-4">
              {/* Search bar for active users */}
              <div className="flex flex-col sm:flex-row gap-4">
                <Input 
                  placeholder="Search active users..." 
                  value={activeSearchTerm}
                  onChange={(e) => setActiveSearchTerm(e.target.value)}
                  className="flex-1 dark:text-white light:text-gray-800"
                />
                <Button 
                  onClick={fetchActiveUsers}
                  variant="outline"
                  className="w-full sm:w-auto"
                >
                  <Activity size={16} className="mr-2" />
                  Refresh
                </Button>
              </div>

              {/* Active Users Content */}
              {activeUsersLoading ? (
                <div className="flex justify-center items-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-500"></div>
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Desktop Table View */}
                  <div className="hidden lg:block">
                    <TableContainer>
                      <table className="w-full">
                        <thead>
                          <tr className="border-b dark:border-gray-700 light:border-gray-200">
                            <th className="text-left p-3 font-medium dark:text-white light:text-gray-800">User</th>
                            <th className="text-left p-3 font-medium dark:text-white light:text-gray-800">Last Activity</th>
                            <th className="text-left p-3 font-medium dark:text-white light:text-gray-800">Login Time</th>
                            <th className="text-left p-3 font-medium dark:text-white light:text-gray-800">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredActiveUsers.map((user) => (
                            <tr key={user.tracker_id} className="border-b dark:border-gray-700 light:border-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700">
                              <td className="p-3">
                                <div className="flex items-center space-x-3">
                                  <UserAvatar username={user.username} size="sm" />
                                  <div>
                                    <div className="font-medium dark:text-white light:text-gray-800">
                                      {user.username}
                                    </div>
                                    <div className="text-sm text-gray-500">
                                      {user.first_name} {user.last_name}
                                    </div>
                                    <div className="flex space-x-1 mt-1">
                                      {user.is_admin && <Badge className="bg-red-500 text-white text-xs">Admin</Badge>}
                                      {user.is_staff && <Badge className="bg-purple-500 text-white text-xs">Staff</Badge>}
                                    </div>
                                  </div>
                                </div>
                              </td>
                              <td className="p-3">
                                <div className="dark:text-white light:text-gray-800">
                                  {formatLastActivity(user.last_activity)}
                                </div>
                                <div className="text-xs text-gray-500">
                                  {new Date(user.last_activity).toLocaleString()}
                                </div>
                              </td>
                              <td className="p-3">
                                <div className="text-sm dark:text-white light:text-gray-800">
                                  {user.login_time ? new Date(user.login_time).toLocaleString() : 'N/A'}
                                </div>
                              </td>
                              <td className="p-3">
                                <Button 
                                  variant="ghost" 
                                  size="sm"
                                  onClick={() => promptForceLogout(user)}
                                  className="text-red-500 hover:text-red-700 hover:bg-red-50"
                                >
                                  <Power size={16} className="mr-1" />
                                  Force Logout
                                </Button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </TableContainer>
                  </div>

                  {/* Mobile Card View */}
                  <div className="lg:hidden space-y-3">
                    {filteredActiveUsers.map((user) => (
                      <Card key={user.tracker_id} className="p-4">
                        <div className="flex items-start justify-between">
                          <div className="flex items-center space-x-3 flex-1">
                            <UserAvatar username={user.username} size="sm" />
                            <div className="flex-1 min-w-0">
                              <div className="font-medium dark:text-white light:text-gray-800 truncate">
                                {user.username}
                              </div>
                              <div className="text-sm text-gray-500 truncate">
                                {user.first_name} {user.last_name}
                              </div>
                              <div className="flex space-x-1 mt-1">
                                {user.is_admin && <Badge className="bg-red-500 text-white text-xs">Admin</Badge>}
                                {user.is_staff && <Badge className="bg-purple-500 text-white text-xs">Staff</Badge>}
                              </div>
                              <div className="text-xs text-gray-400 mt-2">
                                Active: {formatLastActivity(user.last_activity)}
                              </div>
                              <div className="text-xs text-gray-400">
                                Login: {user.login_time ? new Date(user.login_time).toLocaleString() : 'N/A'}
                              </div>
                            </div>
                          </div>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => promptForceLogout(user)}
                            className="text-red-500 hover:text-red-700"
                          >
                            <Power size={16} />
                          </Button>
                        </div>
                      </Card>
                    ))}
                  </div>

                  {filteredActiveUsers.length === 0 && !activeUsersLoading && (
                    <div className="text-center py-8 text-gray-500">
                      No active users found
                    </div>
                  )}
                </div>
              )}
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {/* Force Logout Confirmation Dialog */}
      <AlertDialog open={showForceLogoutDialog} onOpenChange={setShowForceLogoutDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Force Logout User</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to force logout <strong>{userToLogout?.username}</strong>? 
              This will immediately end their session and they will need to log in again.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={forceLoggingOut} onClick={() => { setShowForceLogoutDialog(false); setUserToLogout(null); }}>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleForceLogout}
              disabled={forceLoggingOut}
              className="bg-red-500 hover:bg-red-600 text-white"
            >
              {forceLoggingOut ? "Logging out..." : "Force Logout"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Add User Form (existing implementation) */}
      {showNewUserForm && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <Card className="w-full max-w-md bg-white dark:bg-gray-800 shadow-xl rounded-lg p-6">
            <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium dark:text-white light:text-gray-800">Add New User</h3>
                <Button 
                  variant="ghost" 
                  size="icon" 
                  onClick={() => setShowNewUserForm(false)}
                  className="h-8 w-8"
                >
                  <X size={18} />
                </Button>
              </div>
              
              <form onSubmit={handleCreateUser} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="username" className="dark:text-white light:text-gray-800">Username</Label>
                    <Input 
                      id="username"
                      value={newUser.username}
                      onChange={(e) => setNewUser({...newUser, username: e.target.value})}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="email" className="dark:text-white light:text-gray-800">Email</Label>
                    <Input 
                      id="email"
                      type="email"
                      value={newUser.email}
                      onChange={(e) => setNewUser({...newUser, email: e.target.value})}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="first_name" className="dark:text-white light:text-gray-800">First Name</Label>
                    <Input 
                      id="first_name"
                      value={newUser.first_name}
                      onChange={(e) => setNewUser({...newUser, first_name: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label htmlFor="last_name" className="dark:text-white light:text-gray-800">Last Name</Label>
                    <Input 
                      id="last_name"
                      value={newUser.last_name}
                      onChange={(e) => setNewUser({...newUser, last_name: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label htmlFor="password" className="dark:text-white light:text-gray-800">Password</Label>
                    <Input 
                      id="password"
                      type="password"
                      value={newUser.password}
                      onChange={(e) => setNewUser({...newUser, password: e.target.value})}
                      required
                    />
                  </div>
                </div>
                
                <div className="flex flex-wrap gap-4">
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input 
                      type="checkbox" 
                      checked={newUser.is_admin}
                      onChange={(e) => setNewUser({...newUser, is_admin: e.target.checked})}
                      className="w-4 h-4"
                    />
                    <span className="dark:text-white light:text-gray-800">Admin</span>
                  </label>
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input 
                      type="checkbox" 
                      checked={newUser.is_debater}
                      onChange={(e) => setNewUser({...newUser, is_debater: e.target.checked})}
                      className="w-4 h-4"
                    />
                    <span className="dark:text-white light:text-gray-800">Debater</span>
                  </label>
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input 
                      type="checkbox" 
                      checked={newUser.is_adjudicator}
                      onChange={(e) => setNewUser({...newUser, is_adjudicator: e.target.checked})}
                      className="w-4 h-4"
                    />
                    <span className="dark:text-white light:text-gray-800">Adjudicator</span>
                  </label>
                </div>
                
                <div className="flex justify-end space-x-2 pt-2">
                  <Button 
                    variant="ghost" 
                    type="button" 
                    onClick={() => setShowNewUserForm(false)}
                  >
                    Cancel
                  </Button>
                  <Button type="submit" className="btn-primary" disabled={submitting}>
                    {submitting ? "Adding..." : "Add User"}
                  </Button>
                </div>
              </form>
          </Card>
            </div>
          )}

      {editingUserId && editData && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <Card className="w-full max-w-md bg-white dark:bg-gray-800 shadow-xl rounded-lg p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium dark:text-white light:text-gray-800">Edit User</h3>
              <Button 
                variant="ghost" 
                size="icon" 
                onClick={cancelEditing}
                className="h-8 w-8"
              >
                <X size={18} />
              </Button>
            </div>
            <form onSubmit={e => { e.preventDefault(); handleUpdateUser(editingUserId, editData); }} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="edit-username" className="dark:text-white light:text-gray-800">Username</Label>
                  <Input 
                    id="edit-username"
                    value={editData.username}
                    onChange={e => setEditData({ ...editData, username: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="edit-email" className="dark:text-white light:text-gray-800">Email</Label>
                  <Input 
                    id="edit-email"
                    type="email"
                    value={editData.email}
                    onChange={e => setEditData({ ...editData, email: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="edit-first_name" className="dark:text-white light:text-gray-800">First Name</Label>
                  <Input 
                    id="edit-first_name"
                    value={editData.first_name}
                    onChange={e => setEditData({ ...editData, first_name: e.target.value })}
                  />
                </div>
                <div>
                  <Label htmlFor="edit-last_name" className="dark:text-white light:text-gray-800">Last Name</Label>
                  <Input 
                    id="edit-last_name"
                    value={editData.last_name}
                    onChange={e => setEditData({ ...editData, last_name: e.target.value })}
                  />
                </div>
              </div>
              <div className="flex flex-wrap gap-4">
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input 
                    type="checkbox" 
                    checked={editData.is_admin}
                    onChange={e => setEditData({ ...editData, is_admin: e.target.checked })}
                    className="w-4 h-4"
                  />
                  <span className="dark:text-white light:text-gray-800">Admin</span>
                </label>
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input 
                    type="checkbox" 
                    checked={editData.is_debater}
                    onChange={e => setEditData({ ...editData, is_debater: e.target.checked })}
                    className="w-4 h-4"
                  />
                  <span className="dark:text-white light:text-gray-800">Debater</span>
                </label>
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input 
                    type="checkbox" 
                    checked={editData.is_adjudicator}
                    onChange={e => setEditData({ ...editData, is_adjudicator: e.target.checked })}
                    className="w-4 h-4"
                  />
                  <span className="dark:text-white light:text-gray-800">Adjudicator</span>
                </label>
              </div>
              <div className="flex justify-end space-x-2 pt-2">
                <Button 
                  variant="ghost" 
                  type="button" 
                  onClick={cancelEditing}
                >
                  Cancel
                </Button>
                <Button type="submit" className="btn-primary" disabled={loading}>
                  {loading ? "Saving..." : "Save Changes"}
                </Button>
              </div>
            </form>
          </Card>
        </div>
      )}

      {/* Delete User Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete User</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete <strong>{userToDelete?.username}</strong>? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => { setShowDeleteDialog(false); setUserToDelete(null); }}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDeleteUser} className="bg-red-500 hover:bg-red-600 text-white">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </motion.div>
  );
};

export default UsersPanel;

async function fetchWithAuth(endpoint: string, options: { method: string; body: string; }): Promise<any> {
  const token = localStorage.getItem("token");
  
  const response = await fetch(`${apiConfig.baseURL}/api${endpoint}`, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      "Authorization": `Token ${token}`
    },
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || "API request failed");
  }

  return response.json();
}
